


import {
  defineComponent,
  onMounted,
  onBeforeUnmount,
  computed,
  reactive,
  ref,
} from '@vue/composition-api';

import type {
  EmergencyResponseTeamReport,
  EmergencyResponseTeamState,
} from '@/models/externalReports/emergencyResponseTeam/spEmergencyResponseTeamReport';
import type { EmergencyResponseTeamCar } from '@/models/externalReports/emergencyResponseTeam/emergencyResponseTeamCar';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import { UserActionTypes } from '@/store/modules/user';
import useGeolocation from '@/composables/externalReports/useGeolocation';
import emergencyResponseTeamCarApi from '@/apis/externalReports/emergencyResponseTeam/emergency_response_team_car';
import emergencyResponseTeamMtxApi from '@/apis/externalReports/emergencyResponseTeam/emergency_response_team_mtx';
import { enableNoSleep, disableNoSleep } from '@/lib/noSleepUtil';
import {
  initCommonState,
  notifyWorkElapsedTimeSpeech,
} from '@/components/Sp/ExternalReports/EmergencyResponseTeam/utils';
import {
  timeDifferenceInSeconds,
  secondsToTimeInteger,
  timeInteger,
} from '@/lib/dateTimeHelper';
import useSpeech from '@/composables/useSpeech';
import type {
  EmergencyResponseTeamMtx,
  EmergencyResponseTeamMtxsCreateParams,
} from '@/models/apis/externalReports/emergencyResponseTeam/emergencyResponseTeamMtxRequest';
import { useRequestGuard } from '@/composables/useRequestGuard';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import DepartureScreen from '@/components/Sp/ExternalReports/EmergencyResponseTeam/DepartureScreen/index.vue';
import WorkingScreen from '@/components/Sp/ExternalReports/EmergencyResponseTeam/WorkingScreen/index.vue';
import PhotoMemoScreen from '@/components/Sp/ExternalReports/EmergencyResponseTeam/PhotoMemoScreen/index.vue';
import { dtFormat } from '@/lib/dateHelper';
import { EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/emergencyResponseTeam';
import type { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import { EXTERNAL_REPORT_ABILITY_DRIVER } from '@/consts/externalReports/external_reports';
import { LocalStorageActionTypes, LocalStorageGetterTypes } from '@/store/modules/localStorage';

const EMERGENCY_RESPONSE_TEAM_CAR_UPDATE_TIMER_SEC = 10;
const WORK_ELAPSED_TIME_NOTIFY_TIMER_SEC = 1;
const EMERGENCY_RESPONSE_TEAM_MTX_STORE_TIMER_SEC = 10; // 1秒だとさすがに多すぎた
const EMERGENCY_RESPONSE_TEAM_MTX_CREATE_TIMER_SEC = 60;

export default defineComponent({
  components: {
    DepartureScreen,
    WorkingScreen,
    PhotoMemoScreen,
  },
  setup() {
    const state = reactive<EmergencyResponseTeamState>(initCommonState());
    const store = useStore();
    const { router } = useRoute();
    const userState = store.state.user;
    const { doSpeech } = useSpeech();
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      showErrorMsg(errorMessage);
    });

    const workElapsedTimeInt = computed<number>(() => {
      if (!state.workStartTime || !state.currentTime) { return 0; }

      const workElapsedTimeSec = timeDifferenceInSeconds(state.workStartTime, state.workEndTime ?? state.currentTime);
      return secondsToTimeInteger(workElapsedTimeSec);
    });
    const userSettings = ref<ExternalReportSettings | null>(null);
    const externalReportAbility = computed<ExternalReportAbility | null>(() => {
      return userState?.external_report_abilities?.find(
        ability => ability.external_report_type === EXTERNAL_REPORT_TYPE,
      ) ?? null;
    });
    const statusWorking = computed<string>(() => {
      return userSettings.value?.choices.status.working.key ?? 'working';
    });
    const statusStopped = computed<string>(() => {
      return userSettings.value?.choices.status.stopped.key ?? 'stopped';
    });

    const currentComponent = computed<string>(() => {
      if (state.currentScreenOverride) {
        return state.currentScreenOverride;
      }

      const { currentScreen } = getCurrentScreenAndStatus();
      return currentScreen;
    });
    const isImagesEditing = computed<boolean>(() => {
      return state.currentScreenOverride === 'PhotoMemoScreen';
    });
    const localStorageKey = LocalStorageGetterTypes.EMERGENCY_RESPONSE_TEAM_REPORT_SP_STORAGE;

    const stopAll = async() => {
      clearTimers();
      await doRequest(async() => {
        updateCarStatus(statusStopped.value);
      }, '車両の状態更新に失敗しました。再度操作を行ってください。');
    };
    const geolocationState = useGeolocation(stopAll);
    const currentLocation = computed(() => geolocationState.currentLocation);

    const saveLocalStorage = (): void => {
      store.dispatch(LocalStorageActionTypes.SET, {
        key: localStorageKey,
        val: { emergencyResponseTeamCar: state.emergencyResponseTeamCar },
      });
    };
    const clearLocalStorage = (): void => {
      store.dispatch(LocalStorageActionTypes.SET, { key: localStorageKey, val: null });
    };
    const loadEmergencyResponseTeamCarDataFromLocalStorage = (): void => {
      const stored = store.getters[localStorageKey];
      if (!stored) return;

      state.emergencyResponseTeamCar = stored.emergencyResponseTeamCar;
      state.baseDepartureTime = state.emergencyResponseTeamCar.report.baseDepartureTs ?? null;
      state.workStartTime = state.emergencyResponseTeamCar.report.operationStartTs ?? null;
      state.workEndTime = state.emergencyResponseTeamCar.report.operationEndTs ?? null;
      const { status } = getCurrentScreenAndStatus();
      state.status = status;
    };
    const getCurrentScreenAndStatus = (): { currentScreen: string; status: string } => {
      const report = state.emergencyResponseTeamCar.report;
      if (!report.baseDepartureTs) return { currentScreen: 'DepartureScreen', status: statusStopped.value };
      if (!report.operationEndTs) return { currentScreen: 'WorkingScreen', status: statusWorking.value };

      return { currentScreen: 'DepartureScreen', status: statusStopped.value };
    };
    const updateCommonState = (status: string | null, report: EmergencyResponseTeamReport): void => {
      state.status = status ?? state.status;
      state.emergencyResponseTeamCar.report = report;
      state.baseDepartureTime = report.baseDepartureTs ? report.baseDepartureTs : null;
      state.currentScreenOverride = null;
      saveLocalStorage();
    };
    const updateCommonStateAndCarStatus = async(status: string, report: EmergencyResponseTeamReport): Promise<void> => {
      updateCommonState(status, report);
      await doRequest(async() => {
        await updateCarStatus(status);
      }, '車両の状態更新に失敗しました。再度操作を行ってください。');
    };
    const updateCarStatus = async(status: string): Promise<EmergencyResponseTeamCar | null> => {
      if (!geolocationState.currentLocation) { return null; }

      const { lat, lon } = geolocationState.currentLocation;
      state.status = status ?? state.status;
      const reqObj = {
        lat: lat,
        lon: lon,
        status: state.status,
      };
      const emergencyResponseTeamCar = await emergencyResponseTeamCarApi.createOrUpdate(reqObj);
      state.emergencyResponseTeamCar.id = emergencyResponseTeamCar.id;
      saveLocalStorage();

      return emergencyResponseTeamCar;
    };
    const clearTimers = () => {
      if (state.carUpdateTimer) {
        clearTimeout(state.carUpdateTimer);
      }
      if (state.workElapsedTimeNotifyTimer) {
        clearInterval(state.workElapsedTimeNotifyTimer);
      }
      if (state.mtxStoreTimer) {
        clearInterval(state.mtxStoreTimer);
      }
      if (state.mtxCreateTimer) {
        clearTimeout(state.mtxCreateTimer);
      }
      state.carUpdateTimer = null;
      state.workElapsedTimeNotifyTimer = null;
      state.mtxStoreTimer = null;
      state.mtxCreateTimer = null;
    };
    const storeEmergencyResponseTeamMtx = () => {
      if (!geolocationState.currentLocation) return;
      if (state.emergencyResponseTeamCar.id === 0 || state.emergencyResponseTeamCar.report.id === 0) return;

      const now = new Date();
      if (state.lastEmergencyResponseTeamMtxTs) {
        const threshold = 1000; // milliseconds
        const diffMSec = now.getTime() - state.lastEmergencyResponseTeamMtxTs.getTime();
        if (diffMSec < threshold) return;
      }

      const { lat, lon } = geolocationState.currentLocation;
      const obj: EmergencyResponseTeamMtx = {
        ts: now,
        lat: lat,
        lon: lon,
        status: state.status,
      };
      state.emergencyResponseTeamMtxs.push(obj);
      state.lastEmergencyResponseTeamMtxTs = now;
    };
    const startWorking = async(status: string, report: EmergencyResponseTeamReport): Promise<void> => {
      await updateCommonStateAndCarStatus(status, report);
      await restartAllIntervals();
      state.workStartTime = report.operationStartTs ?? new Date();
    };
    const stopRunning = async(status: string, report: EmergencyResponseTeamReport): Promise<void> => {
      updateCommonState(status, report);
      stopAll();
    };
    const saveEmergencyResponseTeamMtxs = async() => {
      if (state.emergencyResponseTeamMtxs.length < 2) return;

      const emergencyResponseTeamMtxs = state.emergencyResponseTeamMtxs.splice(0);
      const reqObj: EmergencyResponseTeamMtxsCreateParams = {
        car_id: state.emergencyResponseTeamCar.id,
        report_id: state.emergencyResponseTeamCar.report.id,
        mtxs: emergencyResponseTeamMtxs,
      };
      await emergencyResponseTeamMtxApi.createEmergencyResponseTeamMTXs(reqObj);
    };
    const editContentAndRoad = () => {
      state.currentScreenOverride = 'SaveWorkSituationScreen';
    };
    const addPhoto = () => {
      state.currentScreenOverride = 'PhotoMemoScreen';
    };
    const notifyWorkElapsedTime = () => {
      // 現在時刻の更新
      state.currentTime = new Date();
      notifyWorkElapsedTimeSpeech(workElapsedTimeInt.value, doSpeech);
    };
    const startCarStatusLoop = () => {
      const loop = async() => {
        try {
          await updateCarStatus(state.status);
        } catch (error) {
          console.error('Failed to update car status in loop:', error);
        }

        state.carUpdateTimer = window.setTimeout(loop, EMERGENCY_RESPONSE_TEAM_CAR_UPDATE_TIMER_SEC * 1000);
      };
      loop();
    };
    const restartEmergencyResponseTeamCarUpdateInterval = async() => {
      if (state.carUpdateTimer) {
        clearTimeout(state.carUpdateTimer);
      }
      startCarStatusLoop();
    };
    const restartWorkElapsedTimeNotifyInterval = () => {
      if (state.workElapsedTimeNotifyTimer) {
        clearInterval(state.workElapsedTimeNotifyTimer);
      }
      state.workElapsedTimeNotifyTimer = window.setInterval(notifyWorkElapsedTime, WORK_ELAPSED_TIME_NOTIFY_TIMER_SEC * 1000);
    };
    const startMtxCreateLoop = () => {
      const loop = async() => {
        try {
          await saveEmergencyResponseTeamMtxs();
        } catch (error) {
          console.error('Failed to create MTX in loop:', error);
        }

        state.mtxCreateTimer = window.setTimeout(loop, EMERGENCY_RESPONSE_TEAM_MTX_CREATE_TIMER_SEC * 1000);
      };
      loop();
    };
    const restartEmergencyResponseTeamMtxUpdateInterval = () => {
      state.emergencyResponseTeamMtxs = [];
      if (state.mtxStoreTimer) {
        clearInterval(state.mtxStoreTimer);
      }
      if (state.mtxCreateTimer) {
        clearTimeout(state.mtxCreateTimer);
      }
      state.mtxStoreTimer = window.setInterval(storeEmergencyResponseTeamMtx, EMERGENCY_RESPONSE_TEAM_MTX_STORE_TIMER_SEC * 1000);
      startMtxCreateLoop();
    };
    const restartAllIntervals = async() => {
      clearTimers();
      await restartEmergencyResponseTeamCarUpdateInterval();
      restartWorkElapsedTimeNotifyInterval();
      restartEmergencyResponseTeamMtxUpdateInterval();
    };
    const showErrorMsg = (errMsg: string) => {
      state.errorModalMsg = errMsg;
      state.showErrorModal = true;
    };
    const logout = async() => {
      await stopAll();
      await doRequest(async() => {
        await store.dispatch(UserActionTypes.LOGOUT);
      }, 'ログアウトに失敗しました。再度操作を行ってください。');
      router.push({ name: 'Login' });
    };

    onMounted(async() => {
      enableNoSleep();
      loadEmergencyResponseTeamCarDataFromLocalStorage();
      await doRequest(async() => {
        await waitForUserAndMasters();
        userSettings.value = window.master.externalReportSettings?.find(
          setting => setting.external_report_type === EXTERNAL_REPORT_TYPE,
        ) ?? null;
        if (!externalReportAbility.value || !userSettings.value) {
          router.replace({ name: 'Login' });
          return;
        }
        const abilities = userSettings.value?.choices.abilities;
        const ability = abilities?.find(ability => ability.key === externalReportAbility.value?.ability_id);
        if (!ability) {
          router.replace({ name: 'Login' });
          return;
        }

        if (!ability.name.endsWith(EXTERNAL_REPORT_ABILITY_DRIVER)) {
          router.replace({ name: 'Login' });
          return;
        }
        if (state.emergencyResponseTeamCar.report.id !== 0 && state.status !== statusStopped.value) {
          await restartAllIntervals();
        }
        if (!geolocationState.currentLocation) {
          let count = 100;
          while (count-- > 0 && !geolocationState.currentLocation) {
            await new Promise(resolve => window.setTimeout(resolve, 100));
          }
        }
        await updateCarStatus(state.status);
      }, 'ユーザー情報の読み込みに失敗しました。再度操作を行ってください。');
      if (!userSettings.value) {
        router.push({ name: 'Login' });
        return;
      }

      state.externalReportSpLabels = userSettings.value.sp;
    });

    onBeforeUnmount(() => {
      clearTimers();
      clearLocalStorage();
      disableNoSleep();
    });

    return {
      state,
      geolocationState,
      currentComponent,
      currentLocation,
      isRequesting,
      isImagesEditing,
      userState,
      workElapsedTimeInt,
      userSettings,
      externalReportAbility,
      statusStopped,
      addPhoto,
      dtFormat,
      editContentAndRoad,
      logout,
      showErrorMsg,
      startWorking,
      stopRunning,
      timeInteger,
      updateCommonState,
    };
  },
});
